import { useCallback } from 'react'

/**
 * Checks if a hostname is reachable by attempting a no-cors fetch.
 * DNS failures (e.g. typos in domain) reject quickly (<1s).
 * With mode: 'no-cors', even if the server blocks the request or returns an error,
 * the fetch resolves with an opaque response — only genuine network/DNS failures reject.
 */
async function checkHostReachable(url: string, timeoutMs = 5000): Promise<boolean> {
  const normalizedUrl = url.startsWith('http') ? url : `https://${url}`
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    await fetch(normalizedUrl, {
      method: 'HEAD',
      mode: 'no-cors',
      signal: controller.signal,
    })
    return true
  } catch {
    return false
  } finally {
    clearTimeout(timeout)
  }
}

export const useCheckHostReachable = () => {
  return useCallback((url: string) => checkHostReachable(url), [])
}
